import type { PrimitiveNode } from "@/document/model/nodes";
import { normalizeStrokeStack } from "@/document/model/styles";
import type { Rect } from "@/lib/math/rect";

export interface Point {
  x: number;
  y: number;
}

export function primitiveToPolygon(node: PrimitiveNode): Point[] {
  const { x, y, width, height } = node.bounds;

  switch (node.primitive.kind) {
    case "oval": {
      const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
      return ovalOutlinePoints(node.bounds, arcStart, arcSweep, ratio, 48);
    }
    case "polygon":
      return roundedPolygonPoints(node.bounds, node.primitive.sides, node.primitive.curvature ?? 0);
    case "star":
      return starPoints(node.bounds, node.primitive.points, node.primitive.innerRadiusRatio);
    case "line":
      return lineQuad(node.bounds, Math.max(2, ...normalizeStrokeStack(node.style).map(stroke => stroke.width)));
    case "rectangle":
      return roundedRectPoints(node.bounds, node.primitive.cornerRadii);
    case "frame":
    default:
      return [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
      ];
  }
}

export function primitiveToTriangles(node: PrimitiveNode): number[] {
  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    // Full circle with no hole uses the ellipse shader – return empty here
    if (arcSweep >= 360 && ratio === 0) {
      return [];
    }
    return arcOvalTriangles(node.bounds, arcStart, arcSweep, ratio);
  }

  const points = primitiveToPolygon(node);
  if (points.length < 3) {
    return [];
  }

  if (node.primitive.kind === "star") {
    return centerFanTriangles(node.bounds, points);
  }

  if (node.primitive.kind === "polygon" && (node.primitive.curvature ?? 0) > 0) {
    return earclipTriangles(points);
  }

  const origin = points[0]!;
  const rest = points.slice(1);
  const vertices: number[] = [];
  for (let index = 0; index < rest.length - 1; index += 1) {
    const a = rest[index]!;
    const b = rest[index + 1]!;
    vertices.push(origin.x, origin.y, a.x, a.y, b.x, b.y);
  }

  return vertices;
}

export function polygonToSvgPoints(points: Point[]): string {
  return points.map(point => `${round(point.x)},${round(point.y)}`).join(" ");
}

export function primitiveToNetworkPolygon(node: PrimitiveNode): Point[] {
  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    return ovalOutlinePoints(node.bounds, arcStart, arcSweep, ratio, 16);
  }

  return primitiveToPolygon(node);
}

// ─── Arc oval helpers ────────────────────────────────────────────────────────

/** Flat triangle vertex list (x0,y0, x1,y1, x2,y2, …) for an arc/donut oval. */
export function arcOvalTriangles(rect: Rect, arcStart: number, arcSweep: number, ratio: number): number[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = Math.abs(rect.width) / 2;
  const ry = Math.abs(rect.height) / 2;

  const safeSweep = Math.max(1, Math.min(360, arcSweep));
  const safeRatio = Math.max(0, Math.min(0.99, ratio));
  const startRad = (arcStart - 90) * (Math.PI / 180);
  const sweepRad = safeSweep * (Math.PI / 180);
  const isFullCircle = safeSweep >= 360;
  const segments = Math.max(4, Math.round(96 * safeSweep / 360));

  const triangles: number[] = [];

  if (safeRatio > 0) {
    // Ring / donut strip
    for (let i = 0; i < segments; i++) {
      const a1 = startRad + (sweepRad * i) / segments;
      const a2 = isFullCircle
        ? startRad + (sweepRad * (i + 1)) / segments
        : startRad + (sweepRad * (i + 1)) / segments;
      const o1x = cx + Math.cos(a1) * rx;
      const o1y = cy + Math.sin(a1) * ry;
      const o2x = cx + Math.cos(a2) * rx;
      const o2y = cy + Math.sin(a2) * ry;
      const i1x = cx + Math.cos(a1) * rx * safeRatio;
      const i1y = cy + Math.sin(a1) * ry * safeRatio;
      const i2x = cx + Math.cos(a2) * rx * safeRatio;
      const i2y = cy + Math.sin(a2) * ry * safeRatio;
      triangles.push(o1x, o1y, o2x, o2y, i1x, i1y);
      triangles.push(i1x, i1y, o2x, o2y, i2x, i2y);
    }
  } else {
    // Pie / sector fan from center
    for (let i = 0; i < segments; i++) {
      const a1 = startRad + (sweepRad * i) / segments;
      const a2 = startRad + (sweepRad * (i + 1)) / segments;
      const p1x = cx + Math.cos(a1) * rx;
      const p1y = cy + Math.sin(a1) * ry;
      const p2x = cx + Math.cos(a2) * rx;
      const p2y = cy + Math.sin(a2) * ry;
      triangles.push(cx, cy, p1x, p1y, p2x, p2y);
    }
  }

  return triangles;
}

/** Outline polygon for an arc/donut oval (used for hit testing and stroke). */
export function ovalOutlinePoints(rect: Rect, arcStart: number, arcSweep: number, ratio: number, segments: number): Point[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = Math.abs(rect.width) / 2;
  const ry = Math.abs(rect.height) / 2;

  const safeSweep = Math.max(1, Math.min(360, arcSweep));
  const safeRatio = Math.max(0, Math.min(0.99, ratio));
  const startRad = (arcStart - 90) * (Math.PI / 180);
  const sweepRad = safeSweep * (Math.PI / 180);
  const isFullCircle = safeSweep >= 360;
  const arcSegments = Math.max(4, Math.round(segments * safeSweep / 360));
  const pointCount = isFullCircle ? arcSegments : arcSegments + 1;

  if (isFullCircle && safeRatio === 0) {
    return ellipsePoints(rect, segments);
  }

  const outerArc: Point[] = [];
  const innerArc: Point[] = [];

  for (let i = 0; i < pointCount; i++) {
    const frac = isFullCircle ? i / arcSegments : i / arcSegments;
    const angle = startRad + sweepRad * frac;
    outerArc.push({ x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry });
    if (safeRatio > 0) {
      innerArc.push({ x: cx + Math.cos(angle) * rx * safeRatio, y: cy + Math.sin(angle) * ry * safeRatio });
    }
  }

  if (safeRatio > 0) {
    return [...outerArc, ...innerArc.slice().reverse()];
  }

  if (isFullCircle) {
    return outerArc;
  }

  // Pie sector: center + outer arc
  return [{ x: cx, y: cy }, ...outerArc];
}

// ─── SVG path for arc/donut ovals ────────────────────────────────────────────

/** Generate an SVG `d` attribute for an arc/donut oval primitive. */
export function arcOvalToSvgPath(rect: Rect, arcStart: number, arcSweep: number, ratio: number): string {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = Math.abs(rect.width) / 2;
  const ry = Math.abs(rect.height) / 2;

  const safeSweep = Math.max(1, Math.min(360, arcSweep));
  const safeRatio = Math.max(0, Math.min(0.99, ratio));
  const startRad = (arcStart - 90) * (Math.PI / 180);
  const sweepRad = safeSweep * (Math.PI / 180);
  const isFullCircle = safeSweep >= 360;

  const outerStart = { x: cx + Math.cos(startRad) * rx, y: cy + Math.sin(startRad) * ry };
  const outerEnd   = { x: cx + Math.cos(startRad + sweepRad) * rx, y: cy + Math.sin(startRad + sweepRad) * ry };

  const f = (n: number) => round(n);
  const largeArc = safeSweep > 180 ? 1 : 0;

  if (isFullCircle && safeRatio > 0) {
    // Full donut: two ellipse paths
    const innerR = rx * safeRatio;
    const innerRy = ry * safeRatio;
    return [
      `M ${f(cx - rx)} ${f(cy)}`,
      `A ${f(rx)} ${f(ry)} 0 1 0 ${f(cx + rx)} ${f(cy)}`,
      `A ${f(rx)} ${f(ry)} 0 1 0 ${f(cx - rx)} ${f(cy)}`,
      `M ${f(cx - innerR)} ${f(cy)}`,
      `A ${f(innerR)} ${f(innerRy)} 0 1 1 ${f(cx + innerR)} ${f(cy)}`,
      `A ${f(innerR)} ${f(innerRy)} 0 1 1 ${f(cx - innerR)} ${f(cy)}`,
      "Z",
    ].join(" ");
  }

  if (safeRatio > 0) {
    // Partial donut arc
    const innerStart = { x: cx + Math.cos(startRad) * rx * safeRatio, y: cy + Math.sin(startRad) * ry * safeRatio };
    const innerEnd   = { x: cx + Math.cos(startRad + sweepRad) * rx * safeRatio, y: cy + Math.sin(startRad + sweepRad) * ry * safeRatio };
    return [
      `M ${f(outerStart.x)} ${f(outerStart.y)}`,
      `A ${f(rx)} ${f(ry)} 0 ${largeArc} 1 ${f(outerEnd.x)} ${f(outerEnd.y)}`,
      `L ${f(innerEnd.x)} ${f(innerEnd.y)}`,
      `A ${f(rx * safeRatio)} ${f(ry * safeRatio)} 0 ${largeArc} 0 ${f(innerStart.x)} ${f(innerStart.y)}`,
      "Z",
    ].join(" ");
  }

  // Pie sector
  return [
    `M ${f(cx)} ${f(cy)}`,
    `L ${f(outerStart.x)} ${f(outerStart.y)}`,
    `A ${f(rx)} ${f(ry)} 0 ${largeArc} 1 ${f(outerEnd.x)} ${f(outerEnd.y)}`,
    "Z",
  ].join(" ");
}

// ─── Rounded polygon helpers ─────────────────────────────────────────────────

/** Polygon outline with rounded corners (curvature 0–1). */
export function roundedPolygonPoints(rect: Rect, sides: number, curvature: number): Point[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const radius = Math.min(Math.abs(rect.width), Math.abs(rect.height)) / 2;
  const safeSides = Math.max(3, Math.round(sides));
  const safeCurvature = Math.max(0, Math.min(1, curvature));

  if (safeCurvature === 0) {
    return radialPoints(rect, safeSides, 0.5);
  }

  const vertices: Point[] = Array.from({ length: safeSides }, (_, i) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * i) / safeSides;
    return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
  });

  const points: Point[] = [];
  for (let i = 0; i < safeSides; i++) {
    const prev = vertices[(i - 1 + safeSides) % safeSides]!;
    const curr = vertices[i]!;
    const next = vertices[(i + 1) % safeSides]!;

    // Half-edge lengths
    const edgeToPrev = Math.hypot(curr.x - prev.x, curr.y - prev.y);
    const edgeToNext = Math.hypot(curr.x - next.x, curr.y - next.y);
    const maxOffset = Math.min(edgeToPrev, edgeToNext) / 2;
    const offset = maxOffset * safeCurvature;

    if (offset < 0.5) {
      points.push(curr);
      continue;
    }

    // Arc from inset-prev toward inset-next around this vertex
    const fromX = curr.x + (prev.x - curr.x) / edgeToPrev * offset;
    const fromY = curr.y + (prev.y - curr.y) / edgeToPrev * offset;
    const toX = curr.x + (next.x - curr.x) / edgeToNext * offset;
    const toY = curr.y + (next.y - curr.y) / edgeToNext * offset;

    // Approximate the corner arc with a few segments
    const arcSegs = Math.max(2, Math.round(8 * safeCurvature));
    for (let s = 0; s <= arcSegs; s++) {
      const t = s / arcSegs;
      // Quadratic Bezier through (curr) as control point
      const bx = (1 - t) * (1 - t) * fromX + 2 * (1 - t) * t * curr.x + t * t * toX;
      const by = (1 - t) * (1 - t) * fromY + 2 * (1 - t) * t * curr.y + t * t * toY;
      points.push({ x: bx, y: by });
    }
  }

  return points;
}

/** Simple ear-clip triangulation for a (roughly) convex polygon. */
function earclipTriangles(points: Point[]): number[] {
  if (points.length < 3) {
    return [];
  }

  const origin = points[0]!;
  const vertices: number[] = [];
  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    vertices.push(origin.x, origin.y, a.x, a.y, b.x, b.y);
  }

  return vertices;
}

function roundedRectPoints(rect: Rect, radii: [number, number, number, number]): Point[] {
  const width = Math.abs(rect.width);
  const height = Math.abs(rect.height);
  if (width <= 0 || height <= 0) {
    return [];
  }

  const x1 = Math.min(rect.x, rect.x + rect.width);
  const y1 = Math.min(rect.y, rect.y + rect.height);
  const x2 = Math.max(rect.x, rect.x + rect.width);
  const y2 = Math.max(rect.y, rect.y + rect.height);
  const maxRadius = Math.min(width, height) / 2;
  const [topLeft, topRight, bottomRight, bottomLeft] = radii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];

  if (topLeft === 0 && topRight === 0 && bottomRight === 0 && bottomLeft === 0) {
    return [
      { x: x1, y: y1 },
      { x: x2, y: y1 },
      { x: x2, y: y2 },
      { x: x1, y: y2 },
    ];
  }

  return [
    ...cornerArc({ x: x1 + topLeft, y: y1 + topLeft }, topLeft, Math.PI, Math.PI * 1.5),
    ...cornerArc({ x: x2 - topRight, y: y1 + topRight }, topRight, Math.PI * 1.5, Math.PI * 2),
    ...cornerArc({ x: x2 - bottomRight, y: y2 - bottomRight }, bottomRight, 0, Math.PI * 0.5),
    ...cornerArc({ x: x1 + bottomLeft, y: y2 - bottomLeft }, bottomLeft, Math.PI * 0.5, Math.PI),
  ];
}

function cornerArc(center: Point, radius: number, startAngle: number, endAngle: number): Point[] {
  if (radius <= 0) {
    return [{ x: center.x, y: center.y }];
  }

  const quarterArcLength = (Math.PI * radius) / 2;
  const segments = Math.min(64, Math.max(8, Math.ceil(quarterArcLength / 2)));
  return Array.from({ length: segments + 1 }, (_, index) => {
    const t = index / segments;
    const angle = startAngle + (endAngle - startAngle) * t;
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  });
}

function ellipsePoints(rect: Rect, segments: number): Point[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = rect.width / 2;
  const ry = rect.height / 2;
  return Array.from({ length: segments }, (_, index) => {
    const angle = (Math.PI * 2 * index) / segments;
    return { x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry };
  });
}

function radialPoints(rect: Rect, count: number, radiusRatio: number): Point[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const radius = Math.min(rect.width, rect.height) / 2;
  const safeCount = Math.max(3, Math.round(count));

  return Array.from({ length: safeCount }, (_, index) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / safeCount;
    return {
      x: cx + Math.cos(angle) * radius * radiusRatio * 2,
      y: cy + Math.sin(angle) * radius * radiusRatio * 2,
    };
  });
}

function starPoints(rect: Rect, points: number, innerRadiusRatio: number): Point[] {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const outerRadius = Math.min(rect.width, rect.height) / 2;
  const innerRadius = outerRadius * Math.max(0.05, Math.min(0.95, innerRadiusRatio));
  const safePoints = Math.max(3, Math.round(points));

  return Array.from({ length: safePoints * 2 }, (_, index) => {
    const angle = -Math.PI / 2 + (Math.PI * index) / safePoints;
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
  });
}

function centerFanTriangles(rect: Rect, points: Point[]): number[] {
  const center = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
  const vertices: number[] = [];

  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    vertices.push(center.x, center.y, current.x, current.y, next.x, next.y);
  }

  return vertices;
}

function lineQuad(rect: Rect, strokeWidth: number): Point[] {
  const x1 = rect.x;
  const y1 = rect.y;
  const x2 = rect.x + rect.width;
  const y2 = rect.y + rect.height;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const halfWidth = Math.max(0.0001, strokeWidth / 2);
  const offsetX = (-dy / length) * halfWidth;
  const offsetY = (dx / length) * halfWidth;

  return [
    { x: x1 + offsetX, y: y1 + offsetY },
    { x: x2 + offsetX, y: y2 + offsetY },
    { x: x2 - offsetX, y: y2 - offsetY },
    { x: x1 - offsetX, y: y1 - offsetY },
  ];
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
